import { Component } from '@theme/component';

const PREVIEW_NODES = '[data-djg-main-image-name], [data-djg-main-image-cover]';

/**
 * Draws the typed name over the main product image. If the product's image already shows
 * an example name, a patch of nearby fabric (copied from the same image) hides it first.
 * Leaves product form submission unchanged.
 */
class DjgNamePreview extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.enabled !== 'true') return;
    this.section = this.closest('.shopify-section');
    this.input = this.querySelector('input[type="text"]');
    if (!this.section || !this.input) return;
    this.cover = this.#parseCover(this.dataset.cover);
    this.controller = new AbortController();
    this.input.addEventListener('input', () => this.updatePreview(), { signal: this.controller.signal });
    this.section.addEventListener('load', () => this.updatePreview(), { capture: true, signal: this.controller.signal });
    // Web fonts change text width once loaded, so measure again when they arrive.
    document.fonts?.addEventListener('loadingdone', () => this.updatePreview(), { signal: this.controller.signal });
    document.fonts?.ready.then(() => this.updatePreview());
    this.resize = new ResizeObserver(() => this.updatePreview());
    this.resize.observe(this.section);
    this.mutations = new MutationObserver((records) => {
      if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && !node.matches(PREVIEW_NODES)))) this.updatePreview();
    });
    this.mutations.observe(this.section, { childList: true, subtree: true });
    this.updatePreview();
  }

  /** "tx,ty,tw,th,sx,sy,sw,sh" (% of image): area to hide, then the clean area copied over it. */
  #parseCover(value) {
    const numbers = (value || '').split(',').map(Number);
    if (numbers.length !== 8 || numbers.some(n => !Number.isFinite(n)) || numbers[6] <= 0 || numbers[7] <= 0) return null;
    const [x, y, w, h, sx, sy, sw, sh] = numbers;
    return { x, y, w, h, sx, sy, sw, sh };
  }

  updatePreview() {
    const name = this.input.value.trim();
    for (const overlay of this.section.querySelectorAll('media-gallery [data-djg-main-image-name]')) {
      const image = overlay.parentElement.querySelector('img.product-media__image');
      if (!image || !image.naturalWidth) continue;
      const width = image.clientWidth;
      const height = image.clientHeight;
      const scale = getComputedStyle(image).objectFit === 'cover'
        ? Math.max(width / image.naturalWidth, height / image.naturalHeight)
        : Math.min(width / image.naturalWidth, height / image.naturalHeight);
      const drawnWidth = image.naturalWidth * scale;
      const drawnHeight = image.naturalHeight * scale;
      const parentRect = overlay.parentElement.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const imageLeft = imageRect.left - parentRect.left + (width - drawnWidth) / 2;
      const imageTop = imageRect.top - parentRect.top + (height - drawnHeight) / 2;

      if (this.cover) this.#updateCover(overlay, image, imageLeft, imageTop, drawnWidth, drawnHeight, !name);

      overlay.style.left = `${imageLeft + drawnWidth * Number(this.dataset.x) / 100}px`;
      overlay.style.top = `${imageTop + drawnHeight * Number(this.dataset.y) / 100}px`;
      overlay.style.color = this.dataset.color;
      if (this.dataset.font) overlay.style.fontFamily = this.dataset.font;
      overlay.classList.toggle('djg-main-image-name--script', this.dataset.script === 'true');
      if (overlay.textContent !== name) overlay.textContent = name;
      overlay.hidden = !name;
      if (!name) continue;

      // Keep the name on one line and shrink it to fit, however long it is.
      const baseSize = drawnWidth * Number(this.dataset.size) / 100;
      const maxWidth = drawnWidth * Number(this.dataset.maxWidth || 85) / 100;
      overlay.style.whiteSpace = 'nowrap';
      overlay.style.maxWidth = 'none';
      overlay.style.fontSize = `${baseSize}px`;
      // The side padding only makes room for swashes, so leave it out of the fit check.
      const style = getComputedStyle(overlay);
      const textWidth = overlay.scrollWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (textWidth > maxWidth) overlay.style.fontSize = `${baseSize * maxWidth / textWidth}px`;
    }
  }

  /** Hides the image's example name with a feathered patch copied from clean fabric nearby. */
  #updateCover(overlay, image, imageLeft, imageTop, drawnWidth, drawnHeight, hidden) {
    let patch = overlay.parentElement.querySelector(':scope > [data-djg-main-image-cover]');
    if (!patch) {
      patch = document.createElement('span');
      patch.className = 'djg-main-image-cover';
      patch.setAttribute('data-djg-main-image-cover', '');
      patch.setAttribute('aria-hidden', 'true');
      overlay.before(patch);
    }
    const { x, y, w, h, sx, sy, sw, sh } = this.cover;
    const backgroundWidth = drawnWidth * w / sw;
    const backgroundHeight = drawnHeight * h / sh;
    patch.style.left = `${imageLeft + drawnWidth * x / 100}px`;
    patch.style.top = `${imageTop + drawnHeight * y / 100}px`;
    patch.style.width = `${drawnWidth * w / 100}px`;
    patch.style.height = `${drawnHeight * h / 100}px`;
    patch.style.backgroundImage = `url("${image.currentSrc || image.src}")`;
    patch.style.backgroundSize = `${backgroundWidth}px ${backgroundHeight}px`;
    patch.style.backgroundPosition = `${-backgroundWidth * sx / 100}px ${-backgroundHeight * sy / 100}px`;
    patch.hidden = hidden;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.resize?.disconnect();
    this.mutations?.disconnect();
    for (const node of this.section?.querySelectorAll(PREVIEW_NODES) ?? []) node.hidden = true;
  }
}

if (!customElements.get('djg-name-preview')) customElements.define('djg-name-preview', DjgNamePreview);
